/**
 * UK Sterling currency formatting — the single formatting primitive for the app.
 *
 * Display only, and (per the v2 architecture) **view/read-only mode only**. Nothing
 * in here ever changes a stored value, an editable input, or an API payload:
 * callers format at the point of render and keep the raw number for sorting,
 * filtering and arithmetic.
 */

import { isMonetaryField } from "../shared/monetaryFields";

/** Where the app lands today, and the fallback if a setting is ever unusable. */
const FALLBACK_CURRENCY = "GBP";
const FALLBACK_LOCALE = "en-GB";

/**
 * Company currency and locale.
 *
 * Fixed to GBP / en-GB today. They are settings rather than constants so that
 * supporting more currencies later is a wiring change at one place, not a sweep
 * through every call site: the formatter cache is already keyed by currency, and
 * `formatGBP` already takes a per-value `currency` override for records that
 * carry their own `currency_code`.
 *
 * The locale is what actually decides the symbol, its position and the
 * separators — en-GB gives "£1,234.50", de-DE gives "1.234,50 €". Deriving the
 * currency from a chosen locale is a lookup no browser API provides; use the
 * `locale-currency` package (`getCurrency("en-GB") === "GBP"`) rather than
 * hand-rolling the table.
 */
let configuredCurrency: string | null = FALLBACK_CURRENCY;
let configuredLocale = FALLBACK_LOCALE;

export const getCurrencyCode = (): string | null => configuredCurrency;
export const getCurrencyLocale = (): string => configuredLocale;

/**
 * Sets the company currency. An unusable ISO 4217 code is ignored, leaving the
 * current setting intact, so a bad API value can never produce an unformattable
 * Intl option mid-render.
 *
 * `null` is a value here, not a failure: it is Company Details' "None" choice
 * and means "render the amount with no symbol at all" (1,234.50 rather than
 * £1,234.50). It is deliberately distinct from *unconfigured*, which
 * `applyCompanyCurrencySettings` resolves to the GBP default before it ever
 * reaches this setter.
 */
const setCurrencyCode = (code: unknown): string | null => {
  if (code === null) {
    configuredCurrency = null;
    return configuredCurrency;
  }
  const candidate = String(code ?? "").toUpperCase();
  if (/^[A-Z]{3}$/.test(candidate)) configuredCurrency = candidate;
  return configuredCurrency;
};

/** Sets the company locale. An unusable BCP 47 tag is ignored. */
const setCurrencyLocale = (locale: unknown): string => {
  const candidate = String(locale ?? "");
  try {
    // Throws on a malformed tag, so a bad setting can never reach Intl later.
    Intl.NumberFormat.supportedLocalesOf([candidate]);
    if (candidate) configuredLocale = candidate;
  } catch {
    /* keep the current locale */
  }
  return configuredLocale;
};

/** Display precision is configurable to 2, 3 or 4 places. The minimum is always 2. */
export type DecimalPlaces = 2 | 3 | 4;

export const ALLOWED_DECIMAL_PLACES: readonly DecimalPlaces[] = [2, 3, 4];

const MIN_DECIMAL_PLACES = 2;

/**
 * Company-level Decimal Places setting.
 *
 * There is no company-settings field for this yet, so it defaults to 2 — which
 * reproduces the standard Sterling rule exactly. `setCurrencyDecimalPlaces` is the
 * single wiring point: call it once at bootstrap when the setting becomes
 * available, rather than threading a value through every call site.
 */
let configuredDecimalPlaces: DecimalPlaces = 2;

export const getCurrencyDecimalPlaces = (): DecimalPlaces =>
  configuredDecimalPlaces;

const setCurrencyDecimalPlaces = (places: unknown): DecimalPlaces => {
  const numeric = Number(places);
  if ((ALLOWED_DECIMAL_PLACES as readonly number[]).includes(numeric)) {
    configuredDecimalPlaces = numeric as DecimalPlaces;
  }
  // An out-of-range value leaves the current setting untouched rather than
  // producing an unformattable Intl option at render time.
  return configuredDecimalPlaces;
};

/**
 * The shape of the three Company Details display settings as they come back
 * from `GET /masterdata/company-profile` (PascalCase, per that resource's
 * response convention).
 */
export type CompanyCurrencySettings = {
  Region?: string | null;
  Currency?: string | null;
  DecimalDigit?: number | null;
};

/**
 * The single wiring point between the Company Details settings and every
 * formatted amount in the app. Call it once with the company-profile response;
 * everything downstream reads the module state above.
 *
 * **On `Currency: null`.** The architecture doc uses `null` for two different
 * things: "None — show no symbol" (Component 4's field table) and "not
 * configured, fall back to GBP" (Component 4's storage table). One nullable
 * column cannot say both, and getting it wrong would silently strip the £ from
 * every existing tenant that has a company profile but has never opened this
 * new panel. `DecimalDigit` breaks the tie: the form always submits 2, 3 or 4
 * for it — there is no "None" — so a non-null `DecimalDigit` is proof the
 * display settings have been saved at least once, and only then is a null
 * `Currency` read as the deliberate "None" choice.
 */
export const applyCompanyCurrencySettings = (
  settings: CompanyCurrencySettings | null | undefined,
): void => {
  const configured =
    settings?.DecimalDigit !== null && settings?.DecimalDigit !== undefined;

  if (!configured) {
    setCurrencyLocale(FALLBACK_LOCALE);
    setCurrencyCode(FALLBACK_CURRENCY);
    setCurrencyDecimalPlaces(2);
    return;
  }

  setCurrencyLocale(settings?.Region || FALLBACK_LOCALE);
  setCurrencyCode(settings?.Currency ?? null);
  setCurrencyDecimalPlaces(settings?.DecimalDigit);
};

const formatterCache = new Map<string, Intl.NumberFormat>();

/**
 * Memoised formatter. `new Intl.NumberFormat()` is expensive relative to
 * `.format()`, and grids render thousands of monetary cells per page.
 */
const getFormatter = (
  currency: string | null,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
  localeOverride?: string,
): Intl.NumberFormat => {
  // `null` means "no symbol" and must survive; "" means "not supplied here",
  // which falls back to the company setting.
  const code = currency === null ? null : currency || configuredCurrency;
  const upper = code === null ? null : code.toUpperCase();
  const locale = localeOverride || configuredLocale;
  const key = `${locale}:${upper ?? "NONE"}:${minimumFractionDigits}:${maximumFractionDigits}`;

  const cached = formatterCache.get(key);
  if (cached) return cached;

  // No currency configured — a plain grouped decimal, so the separators and
  // digit grouping still follow the chosen region, just without a symbol.
  if (upper === null) {
    const plain = new Intl.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    });
    formatterCache.set(key, plain);
    return plain;
  }

  let formatter: Intl.NumberFormat;
  try {
    formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: upper,
      minimumFractionDigits,
      maximumFractionDigits,
    });
  } catch {
    // Invalid/unsupported ISO code — render as Sterling rather than throwing
    // mid-render.
    formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: FALLBACK_CURRENCY,
      minimumFractionDigits,
      maximumFractionDigits,
    });
  }

  formatterCache.set(key, formatter);
  return formatter;
};

/**
 * Reads a number out of whatever a monetary field actually holds.
 *
 * Several monetary fields are still declared `type: "text"` in the served form
 * metadata (`current_value`, `property_value`, `surrender_value`, …), so their
 * stored content is whatever was typed: "1,250.00", "£500", "(350.25)". Those
 * are real amounts and must still render as amounts, so grouping separators,
 * currency symbols, spaces and accounting-style negatives are all stripped
 * before parsing. `Number()` alone returns NaN for every one of them.
 *
 * Returns null when there is no amount in there at all ("TBC", "N/A", "") —
 * which is how callers tell "not a number" from "zero".
 */
export const parseGBP = (
  input: number | string | null | undefined,
): number | null => {
  if (input === null || input === undefined) return null;
  if (typeof input === "number") return Number.isFinite(input) ? input : null;

  const cleaned = input
    .replace(/[£$€,\s]/g, "")
    // "(350.25)" is an accounting-style negative.
    .replace(/^\((.*)\)$/, "-$1")
    .trim();

  if (cleaned === "" || cleaned === "-") return null;

  const numeric = Number(cleaned);
  return Number.isFinite(numeric) ? numeric : null;
};

export type FormatGBPOptions = {
  /** Rendered when the value is absent or unparseable. Defaults to "". */
  blank?: string;
  /**
   * ISO 4217 code. Defaults to the company currency; no conversion is performed.
   * An explicit `null` renders the amount with no symbol.
   */
  currency?: string | null;
  /**
   * BCP 47 tag. Defaults to the company region. Overriding it is for previewing
   * a not-yet-saved setting (Company Details); ordinary screens leave it unset.
   */
  locale?: string;
  /** Overrides the configured Decimal Places setting for this call. */
  decimalPlaces?: DecimalPlaces;
  /**
   * Variable precision: show at least 2 decimals and up to `decimalPlaces`,
   * dropping unnecessary trailing zeros (1000.1200 → £1,000.12, 1000.123 →
   * £1,000.123). Without it the value is shown at exactly `decimalPlaces`.
   *
   * With the default setting of 2 both modes are identical.
   */
  preservePrecision?: boolean;
};

/**
 * Formats a monetary value as UK Sterling: £1,250.50 / -£350.25 / £0.00.
 *
 * Absent values (null/undefined/"") render as `blank` — deliberately NOT "£0.00",
 * since "not recorded" and "nil value" are different facts. Pass
 * `{ blank: "£0.00" }` where a screen wants a zero placeholder.
 */
export const formatGBP = (
  value: number | string | null | undefined,
  options: FormatGBPOptions = {},
): string => {
  const blank = options.blank ?? "";

  // Lenient on purpose: a monetary field declared `type: "text"` can hold
  // "1,250.00" or "£500", and those must render as amounts rather than vanish.
  const numeric = parseGBP(value);
  if (numeric === null) return blank;

  // -0 would otherwise render as "-£0.00".
  const normalised = numeric === 0 ? 0 : numeric;

  const maximumFractionDigits =
    options.decimalPlaces ?? configuredDecimalPlaces;
  const minimumFractionDigits = options.preservePrecision
    ? MIN_DECIMAL_PLACES
    : maximumFractionDigits;

  // `??` would collapse an explicit `currency: null` ("no symbol") into the
  // company setting, so only `undefined` means "not supplied".
  const currency =
    options.currency === undefined ? configuredCurrency : options.currency;

  return getFormatter(
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
    options.locale,
  ).format(normalised);
};

/**
 * True when a value holds an amount and can therefore be rendered as money.
 *
 * Uses the same lenient rule as `parseGBP`, so "1,250.00" and "£500" count as
 * amounts while "TBC", "N/A" and "" do not. Generic renderers use this to tell
 * "format it" from "leave it exactly as it is".
 */
export const isFormattableAmount = (value: unknown): boolean => {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string") return parseGBP(value) !== null;
  return false;
};

/**
 * Formats a value that is *supposed* to be money but might not be.
 *
 * An amount — including one stored as "1,250.00" in a text field — is formatted.
 * Anything else is returned untouched rather than blanked, so free-text content
 * ("TBC", "Varies") in a monetary field stays on screen. Losing it would read as
 * data loss, which is the one thing a display-only change must never do.
 */
export const formatGBPOrPassThrough = (
  value: unknown,
  options: FormatGBPOptions = {},
): unknown =>
  isFormattableAmount(value)
    ? formatGBP(value as number | string, options)
    : value;

/**
 * Renders one value of a read-only label/value detail panel or drawer.
 *
 * Monetary fields (per the registry) render as UK Sterling; everything else keeps
 * its raw value and the panel's placeholder. A genuine `0` renders as "£0.00"
 * rather than the placeholder, per the acceptance criteria, while a monetary
 * field holding non-numeric text keeps that text — see
 * `formatGBPOrPassThrough`.
 *
 * View-mode only — never used for an editable value.
 */
export const formatDetailValue = (
  field: string,
  value: unknown,
  options: FormatGBPOptions & { table?: string; placeholder?: string } = {},
): unknown => {
  const placeholder = options.placeholder ?? "-";
  if (!isMonetaryField(field, options.table)) return value || placeholder;

  // Absent stays the placeholder; a real 0 must still reach the formatter.
  if (value === null || value === undefined || value === "") return placeholder;

  return isFormattableAmount(value)
    ? formatGBP(value as number | string, {
        ...options,
        blank: options.blank ?? placeholder,
      })
    : value;
};
